import React, { FC, useState, useRef, useEffect, useMemo, Profiler } from 'react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import timeGridPlugin from '@fullcalendar/timegrid'

import ModalLabel from './ModalLabel'
import ViewEvent from './Event'

import { useDispatch } from '../../store'
import { setPostDateAction } from '../../store/calendar/slice'

import { dateIsAfter, dateIsToday, formatDateToCellRefs } from './helpers'

import './Calendar.scss'
import { format } from 'date-fns'
import { CALENDAT_VIEW } from '../../store/application/types'
import { setScheduleIdAction, setTimeAction, setDateAction} from '../../store/managePost/slice';

type BigCalendarProps = {
  calendarRef: any
  setCalendarApi: any
  posts: any
  fetchEvents: any
  project: any
  labels: any
  filters: any
  selectedSchedules: any
  setSelectedSchedules: any
  setIsAddingPost: any
  calendarApi: any
  setChekedDate: any
  calendarDataRefs: any
  updateSchedules: any
}

const BigCalendar: FC<BigCalendarProps> = ({
  setCalendarApi,
  labels,
  fetchEvents,
  project,
  posts,
  selectedSchedules,
  setSelectedSchedules,
  setIsAddingPost,
  calendarApi,
  setChekedDate,
  filters,
  calendarDataRefs,
  updateSchedules
}) => {
  const dispatch = useDispatch()

  // POSTS/LABELS
  // Transform posts and labels from store to calendar
  const { transformPostsToCalendar, transformLabelsToCalendar } = calendarDataRefs.current

  const transformedPosts = useMemo(
    () => transformPostsToCalendar(posts),
    [posts, transformPostsToCalendar]
  )
  const transformedLabels = useMemo(
    () => transformLabelsToCalendar(labels),
    [labels, transformLabelsToCalendar]
  )

  const setSelectedEvent = (event: any, shouldBeIncluded: boolean) => {
    const isFindedEvent = (selectedSchedules as any).find(
      (selectedEvt: any) => selectedEvt.id === Number(event.id)
    )

    if (!isFindedEvent) {
      dispatch(setScheduleIdAction(event.extendedProps.schedule.id))
      dispatch(setDateAction(event.extendedProps.schedule.date))
      dispatch(setTimeAction(event.extendedProps.schedule.time))
      setSelectedSchedules([...selectedSchedules, event.extendedProps.schedule])
    } else if (!shouldBeIncluded) {
      // !shouldBeIncluded: if dragging was started by unselected element
      setSelectedSchedules(
        selectedSchedules.filter(
          (selectedSchedule: any) => selectedSchedule.id !== Number(event.id)
        )
      )
    }
  }

  const currentDate = useRef<any>(null)

  const changeDateRange = async (props: any) => {
    if (calendarApi) {
      const start = formatDateToCellRefs(calendarApi.view.activeStart)
      const end = formatDateToCellRefs(calendarApi.view.activeEnd)
      if (currentDate.current && currentDate.current !== start) {
        fetchEvents({ start, end, id: project.id })
        calendarDataRefs.current.deleteEventRefs()
      }
      currentDate.current = start
    }
  }

  useEffect(() => {
    calendarDataRefs.current.deleteEventRefs()
  }, [transformedPosts, transformedLabels, filters, calendarDataRefs])

  useEffect(() => {
    if (calendarApi) {
      const start = formatDateToCellRefs(calendarApi.view.activeStart)
      const end = formatDateToCellRefs(calendarApi.view.activeEnd)
      fetchEvents({ id: project.id, start, end })
      currentDate.current = start;
    }
  }, [fetchEvents, project.id, calendarApi])

  // FILTERS
  const filteredEvents = useMemo(() => {
    const filteredLabels = transformedLabels.filter((label: any) => {
      return !!filters.label.find(({ id, checked }: any) => {
        return id === label.id && checked
      })
    })

    const isFilterTouched = (filter: any) => filter.every(({ checked }: any) => !checked)

    const filteredSchedules = transformedPosts.filter(({ schedule: { post } }: any) => {
      const isIncludeStatus =
        isFilterTouched(filters.status) ||
        !!filters.status.find(
          ({ id, values, checked }: any) =>
            (id.toLowerCase() === post.status.toLowerCase() ||
              post.post_socials.some((socialPost: any) => values.includes(socialPost.status))) &&
            checked
        )

      const isIncludeType =
        isFilterTouched(filters.social_post_type) ||
        !!filters.social_post_type.find(
          ({ checked, values }: any) =>
            values.some((type: string) => type === post.type.toLowerCase()) && checked
        )

      return isIncludeStatus && isIncludeType
    })

    const filteredSchedulesByPost = filteredSchedules.map((item: any) => {
      return {
        ...item,
        schedule: {
          ...item.schedule,
          post: {
            ...item.schedule.post,
            post_socials: item.schedule.post.post_socials
              .filter((post: any) => {
                let includes = isFilterTouched(filters.social_network)

                if (!includes) {
                  filters.social_network.forEach((filter: any) => {
                    if (post.social_network === filter.id && filter.checked) includes = true
                  })
                }
                return includes
              })
              .filter((post: any) => {
                let includes = isFilterTouched(filters.status)

                if (!includes) {
                  filters.status.forEach((filter: any) => {
                    if (
                      filter.values.some((type: string) => type === post.status) &&
                      filter.checked
                    )
                      includes = true
                  })
                }
                return includes
              })
          }
        }
      }
    })

    return [...filteredSchedulesByPost, ...filteredLabels]
  }, [transformedPosts, transformedLabels, filters])

  // DRAG AND DROP EVENTS
  const [dragElementId, setDragElement] = useState<null | string>(null)

  const startDragging = ({ el, event }: any) => {
    setDragElement(event.id)
    setSelectedEvent(event, true)
    for (let selectedEvt of selectedSchedules) {
      calendarDataRefs.current.setDraggingEvent(selectedEvt)
    }
  }

  const stopDragging = ({ el, event }: any) => {
    setDragElement(null)
    for (let selectedEvt of selectedSchedules) {
      calendarDataRefs.current.removeDraggingEvent(selectedEvt)
    }
  }

  const dropEvents = async (props: any) => {
    const scheduleToUpdateIds: any[] = []
    const date = formatDateToCellRefs(props.event.start)
    filteredEvents.forEach((event: any) => {
      for (let selectedEvt of selectedSchedules) {
        if (selectedEvt.id === event.id) {
          if (dateIsAfter(new Date(selectedEvt.date + 'T' + selectedEvt.time))) {
            // send requests to update dates
            const {
              schedule: { is_autoposting, time, id }
            } = event
            scheduleToUpdateIds.push({
              schedule_id: id,
              date,
              is_autoposting: !!is_autoposting,
              time
            })
            dispatch(
              setPostDateAction({
                id: event.id,
                date
              })
            )
          }
        }
      }
    })
    await updateSchedules(scheduleToUpdateIds)
    setSelectedSchedules([])
  }

  const clickOnDate = ({ dateStr, jsEvent, ...props }: any) => {
    const isIcon = jsEvent.target.nodeName === 'svg'
    if (
      !isIcon &&
      (dateIsAfter(dateStr) || dateIsToday(dateStr)) &&
      !calendarDataRefs.current.isHasContent(dateStr)
    ) {
      setChekedDate(dateStr)
      setIsAddingPost(true)
    }
  }

  // DATA TO COMPONENTS
  const propsToCustomEvent = {
    selectedSchedules,
    calendarDataRefs,
    dragElementId,
    setSelectedEvent,
    project
  }
  // const profillerOnRender: any = (eventId: any, ...props: any[]) => {
  //   const [id, phase, actualDuration, baseDuration, startTime, commitTime, interactions] = props
  //   if (eventId == 1070) {
  //     console.log(`[__ Profiller __] ${id} =`, { id, phase, actualDuration, baseDuration, startTime, commitTime, interactions })
  //   }

  // }
  return (
    <>
      <FullCalendar
        ref={(calendar) => setCalendarApi(calendar?.getApi())}
        plugins={[dayGridPlugin, interactionPlugin, timeGridPlugin]}
        headerToolbar={false}
        dayHeaderFormat={{ weekday: 'long' }}
        dayHeaderContent={({ text, date, isToday, view: { type }, ...args }) => (
          <div
            key={`day-header-${date}`}
            className={`main-calendar__week-name ${isToday && 'active'}`}
          >
            {text} {CALENDAT_VIEW.WEEK_VIEW === type && format(date, 'dd')}
          </div>
        )}
        initialView={CALENDAT_VIEW.MONTH_VIEW}
        weekends={true}
        defaultTimedEventDuration='00:01'
        events={filteredEvents}
        editable={true}
        selectable={false}
        navLinks={false}
        fixedWeekCount={false}
        dayCellContent={({ dayNumberText, date }) => {
          return (
            <>
              <ModalLabel {...{ calendarDataRefs, project, date_start: date, date_end: date }} />
              <div
                className='fc-daygrid-day-number__add'
                onClick={() => {
                  setChekedDate(formatDateToCellRefs(date))
                  setIsAddingPost(true)
                }}
              ></div>
              {dayNumberText}
            </>
          )
        }}
        // handle drag events
        eventDragMinDistance={30}
        eventDragStart={startDragging}
        eventDragStop={stopDragging}
        eventDrop={dropEvents}
        // touch delay to dragg
        longPressDelay={200}
        // callback before each set date
        datesSet={changeDateRange}
        eventAllow={(args) => dateIsAfter(args.start)}
        dayCellDidMount={calendarDataRefs.current.setDayCell}
        eventContent={(props) => (
          <div key={`calendar-event-${props.event.id}`} className='calendar-event-wrapper'>
            {/* <Profiler id='CalendarEvent' onRender={(...props2) => profillerOnRender(props.event.id, ...props2)}> */}
            <ViewEvent eventContent={props} {...propsToCustomEvent} />
            {/* </Profiler> */}
          </div>
        )}
        // click on the cell
        dateClick={(options) => clickOnDate(options)}
      />
    </>
  )
}

export default BigCalendar
